import { useState, useCallback, useMemo } from 'react';
import DateTime from '@dealroadshow/uikit-date-time';
import { UTC_TIME_ZONE } from '@framework/timezone/constants';
import { YEAR_MONTH_DAY_DASH_FORMAT } from '@dealroadshow/uikit-date-time/dateFormats';

type TStringKeys<T> = {
  [K in keyof T]: T[K] extends string | null ? K : never;
}[keyof T];

interface IFilterContext<T> {
  localFilters: T,
  setLocalFilters: (updater: T | ((prev: T) => T)) => void,
}

interface IDateBounds {
  from?: string,
  to?: string,
}

interface IUseDateRangeFilterProps<T> {
  fromKey: TStringKeys<T>,
  toKey: TStringKeys<T>,
  bounds?: IDateBounds | null,
}

interface IUseDateRangeFilterReturn {
  fromDate: Date | null,
  toDate: Date | null,
  minDate: Date | null,
  maxDate: Date | null,
  handleFromChange: (date: Date | null) => void,
  handleToChange: (date: Date | null) => void,
}

// Parse plain date string ("2026-05-21") to local Date for DatePicker.
// getDateWithTimeZoneOffset preserves the clock time without shifting to browser timezone.
const parseDate = (value: string | null): Date | null => {
  if (!value) {
    return null;
  }
  const dt = new DateTime(value, { timeZone: UTC_TIME_ZONE }).getDateWithTimeZoneOffset();
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
};

// Format Date to plain date string ("2026-05-21") — no time, no timezone
const formatDate = (date: Date | null): string | null => {
  if (!date || isNaN(date.getTime())) {
    return null;
  }
  return new DateTime(date).format(YEAR_MONTH_DAY_DASH_FORMAT);
};

const createUseDateRangeFilter = <T extends Record<string, unknown>>(
  useFilterContext: () => IFilterContext<T>,
) => (
  { fromKey, toKey, bounds }: IUseDateRangeFilterProps<T>,
): IUseDateRangeFilterReturn => {
  const { localFilters, setLocalFilters } = useFilterContext();

  const [fromDate, setFromDate] = useState<Date | null>(() => parseDate(localFilters[fromKey] as string | null));
  const [toDate, setToDate] = useState<Date | null>(() => parseDate(localFilters[toKey] as string | null));

  const minDate = useMemo(() => parseDate(bounds?.from ?? null), [bounds?.from]);
  const maxDate = useMemo(() => parseDate(bounds?.to ?? null), [bounds?.to]);

  const handleFromChange = useCallback((date: Date | null): void => {
    const validDate = date && !isNaN(date.getTime()) ? date : null;
    setFromDate(validDate);
    setLocalFilters((prev) => ({ ...prev, [fromKey]: formatDate(validDate) }));
  }, [fromKey, setLocalFilters]);

  const handleToChange = useCallback((date: Date | null): void => {
    const validDate = date && !isNaN(date.getTime()) ? date : null;
    setToDate(validDate);
    setLocalFilters((prev) => ({ ...prev, [toKey]: formatDate(validDate) }));
  }, [toKey, setLocalFilters]);

  return { fromDate, toDate, minDate, maxDate, handleFromChange, handleToChange };
};

export { createUseDateRangeFilter, parseDate };
export type { IUseDateRangeFilterProps, IUseDateRangeFilterReturn, IDateBounds };
